package com.devsusana.portfolio.components.sections

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.widgets.SocialLinks
import com.devsusana.portfolio.components.widgets.TechBadge
import com.devsusana.portfolio.i18n.rememberStrings
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.P

/**
 * Hero section introducing Susana Córdoba, her role, technical pillars, and primary calls to action.
 */
@Composable
fun HeroSection(modifier: Modifier = Modifier) {
    val colorMode = ColorMode.current
    val strings = rememberStrings()

    Column(
        modifier = modifier
            .fillMaxWidth()
            .id("home")
            .padding(topBottom = 3.cssRem),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(1.5.cssRem)
    ) {
        // Eyebrow Pill / Lema destacado
        Box(
            modifier = Modifier
                .borderRadius(999.px)
                .backgroundColor(
                    if (colorMode.isDark) com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.12f)
                    else com.varabyte.kobweb.compose.ui.graphics.Color.rgba(61, 220, 132, 0.1f)
                )
                .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.androidGreen)
                .padding(leftRight = 1.cssRem, topBottom = 0.4.cssRem)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(0.5.cssRem)
            ) {
                SpanText("⚡", modifier = Modifier.fontSize(0.85.cssRem))
                SpanText(
                    text = strings.heroTagline,
                    modifier = Modifier
                        .color(SitePalette.androidGreen)
                        .fontSize(0.85.cssRem)
                        .fontWeight(FontWeight.Bold)
                )
            }
        }

        // Main Title (H1 for SEO) - Fluid responsive typography
        H1(attrs = {
            style {
                property("text-align", "center")
                property("margin", "0")
            }
        }) {
            SpanText(
                text = "Susana Córdoba",
                modifier = Modifier
                    .color(SitePalette.textPrimary(colorMode))
                    .styleModifier { property("font-size", "clamp(1.35rem, 4.5vw, 1.85rem)") }
                    .fontWeight(FontWeight.ExtraBold)
            )
            SpanText(
                text = " — ",
                modifier = Modifier
                    .color(SitePalette.androidGreen)
                    .styleModifier { property("font-size", "clamp(1.35rem, 4.5vw, 1.85rem)") }
                    .fontWeight(FontWeight.Bold)
            )
            SpanText(
                text = "Senior Android Engineer & Tech Lead",
                modifier = Modifier
                    .fontWeight(FontWeight.ExtraBold)
                    .styleModifier {
                        property("font-size", "clamp(1.35rem, 4.5vw, 1.85rem)")
                        property("background", "linear-gradient(135deg, #3DDC84 0%, #38BDF8 100%)")
                        property("-webkit-background-clip", "text")
                        property("-webkit-text-fill-color", "transparent")
                    }
            )
        }

        // Subtitle (Bio)
        P {
            SpanText(
                text = strings.heroSubtitle,
                modifier = Modifier
                    .color(SitePalette.textSecondary(colorMode))
                    .styleModifier { property("font-size", "clamp(0.95rem, 2.5vw, 1.05rem)") }
                    .maxWidth(750.px)
                    .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
                    .lineHeight(1.65.cssRem)
            )
        }

        // Badges con flex-wrap para que se adapten con fluidez
        Row(
            modifier = Modifier
                .padding(topBottom = 0.25.cssRem)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("justify-content", "center")
                    property("gap", "0.6rem")
                },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            TechBadge("Kotlin", highlight = true)
            TechBadge("Jetpack Compose", highlight = true)
            TechBadge("Clean Architecture")
            TechBadge("Agentic Programming", highlight = true)
            TechBadge("Legacy Modernization")
            TechBadge("Offline-First")
        }

        // Action Buttons with responsive wrap
        Row(
            modifier = Modifier
                .padding(top = 0.5.cssRem)
                .styleModifier {
                    property("flex-wrap", "wrap")
                    property("justify-content", "center")
                    property("gap", "0.75rem")
                },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Link(
                path = "#hometutorpro",
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                modifier = Modifier
                    .backgroundColor(SitePalette.androidGreen)
                    .padding(leftRight = 1.5.cssRem, topBottom = 0.75.cssRem)
                    .borderRadius(8.px)
                    .styleModifier {
                        property("box-shadow", "0 4px 14px rgba(61, 220, 132, 0.3)")
                    }
            ) {
                SpanText(
                    text = strings.heroCtaHomeTutor,
                    modifier = Modifier
                        .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x0F, 0x17, 0x2A))
                        .fontWeight(FontWeight.Bold)
                        .fontSize(1.cssRem)
                )
            }

            Link(
                path = "#agentic",
                variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                modifier = Modifier
                    .backgroundColor(SitePalette.surface(colorMode))
                    .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                    .padding(leftRight = 1.5.cssRem, topBottom = 0.75.cssRem)
                    .borderRadius(8.px)
            ) {
                SpanText(
                    text = strings.heroCtaAgentic,
                    modifier = Modifier
                        .color(SitePalette.textPrimary(colorMode))
                        .fontWeight(FontWeight.SemiBold)
                        .fontSize(1.cssRem)
                )
            }
        }

        // Social Links below CTA
        SocialLinks(modifier = Modifier.padding(top = 1.cssRem))
    }
}
